"use client";

import { c as _c } from "react/compiler-runtime";
import { animate } from "motion/react";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
interface GlowingEffectProps {
  blur?: number;
  inactiveZone?: number;
  proximity?: number;
  spread?: number;
  variant?: "default" | "white";
  glow?: boolean;
  className?: string;
  disabled?: boolean;
  movementDuration?: number;
  borderWidth?: number;
}
const GlowingEffect = memo(t0 => {
  const $ = _c(36);
  const {
    blur: t1,
    inactiveZone: t2,
    proximity: t3,
    spread: t4,
    variant: t5,
    glow: t6,
    className,
    movementDuration: t7,
    borderWidth: t8,
    disabled: t9
  } = t0;
  const blur = t1 === undefined ? 0 : t1;
  const inactiveZone = t2 === undefined ? 0.7 : t2;
  const proximity = t3 === undefined ? 0 : t3;
  const spread = t4 === undefined ? 20 : t4;
  const variant = t5 === undefined ? "default" : t5;
  const glow = t6 === undefined ? false : t6;
  const movementDuration = t7 === undefined ? 2 : t7;
  const borderWidth = t8 === undefined ? 1 : t8;
  const disabled = t9 === undefined ? true : t9;
  const containerRef = useRef(null);
  let t10;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      x: 0,
      y: 0
    };
    $[0] = t10;
  } else {
    t10 = $[0];
  }
  const lastPosition = useRef(t10);
  const animationFrameRef = useRef(0);
  const [isVisible, setIsVisible] = useState(false);
  let t11;
  let t12;
  if ($[1] !== disabled) {
    t11 = () => {
      const element = containerRef.current;
      if (!element || disabled) {
        return;
      }
      const observer = new IntersectionObserver(t13 => {
        const [entry] = t13;
        return setIsVisible(entry.isIntersecting);
      }, {
        rootMargin: "100px"
      });
      observer.observe(element);
      return () => observer.disconnect();
    };
    t12 = [disabled];
    $[1] = disabled;
    $[2] = t11;
    $[3] = t12;
  } else {
    t11 = $[2];
    t12 = $[3];
  }
  useEffect(t11, t12);
  let t13;
  if ($[4] !== inactiveZone || $[5] !== movementDuration || $[6] !== proximity) {
    t13 = e => {
      if (!containerRef.current) {
        return;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = requestAnimationFrame(() => {
        const element_0 = containerRef.current;
        if (!element_0) {
          return;
        }
        const {
          left,
          top,
          width,
          height
        } = element_0.getBoundingClientRect();
        const mouseX = e?.x ?? lastPosition.current.x;
        const mouseY = e?.y ?? lastPosition.current.y;
        if (e) {
          lastPosition.current = {
            x: mouseX,
            y: mouseY
          };
        }
        const center = [left + width * 0.5, top + height * 0.5];
        const distanceFromCenter = Math.hypot(mouseX - center[0], mouseY - center[1]);
        const inactiveRadius = 0.5 * Math.min(width, height) * inactiveZone;
        if (distanceFromCenter < inactiveRadius) {
          element_0.style.setProperty("--active", "0");
          return;
        }
        const isActive = mouseX > left - proximity && mouseX < left + width + proximity && mouseY > top - proximity && mouseY < top + height + proximity;
        element_0.style.setProperty("--active", isActive ? "1" : "0");
        if (!isActive) {
          return;
        }
        const currentAngle = parseFloat(element_0.style.getPropertyValue("--start")) || 0;
        const targetAngle = 180 * Math.atan2(mouseY - center[1], mouseX - center[0]) / Math.PI + 90;
        const angleDiff = (targetAngle - currentAngle + 180) % 360 - 180;
        const newAngle = currentAngle + angleDiff;
        animate(currentAngle, newAngle, {
          duration: movementDuration,
          ease: [0.16, 1, 0.3, 1],
          onUpdate: value => {
            element_0.style.setProperty("--start", String(value));
          }
        });
      });
    };
    $[4] = inactiveZone;
    $[5] = movementDuration;
    $[6] = proximity;
    $[7] = t13;
  } else {
    t13 = $[7];
  }
  const handleMove = t13;
  let t14;
  let t15;
  if ($[8] !== disabled || $[9] !== handleMove || $[10] !== isVisible) {
    t14 = () => {
      if (disabled || !isVisible) {
        return;
      }
      const handleScroll = () => handleMove();
      const handlePointerMove = e_0 => handleMove(e_0);
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      document.body.addEventListener("pointermove", handlePointerMove, {
        passive: true
      });
      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        window.removeEventListener("scroll", handleScroll);
        document.body.removeEventListener("pointermove", handlePointerMove);
      };
    };
    t15 = [handleMove, disabled, isVisible];
    $[8] = disabled;
    $[9] = handleMove;
    $[10] = isVisible;
    $[11] = t14;
    $[12] = t15;
  } else {
    t14 = $[11];
    t15 = $[12];
  }
  useEffect(t14, t15);
  const t16 = glow && "opacity-100";
  const t17 = variant === "white" && "border-white";
  const t18 = disabled && "!block";
  let t19;
  if ($[13] !== t16 || $[14] !== t17 || $[15] !== t18) {
    t19 = cn("pointer-events-none absolute -inset-px hidden rounded-[inherit] border opacity-0 transition-opacity", t16, t17, t18);
    $[13] = t16;
    $[14] = t17;
    $[15] = t18;
    $[16] = t19;
  } else {
    t19 = $[16];
  }
  let t20;
  if ($[17] !== t19) {
    t20 = <div className={t19} />;
    $[17] = t19;
    $[18] = t20;
  } else {
    t20 = $[18];
  }
  const t21 = `${blur}px`;
  const t22 = `${borderWidth}px`;
  const t23 = variant === "white" ? "repeating-conic-gradient(\n                  from 236.84deg at 50% 50%,\n                  var(--black),\n                  var(--black) calc(25% / var(--repeating-conic-gradient-times))\n                )" : "radial-gradient(circle, #dd7bbb 10%, #dd7bbb00 20%),\n                radial-gradient(circle at 40% 40%, #d79f1e 5%, #d79f1e00 15%),\n                radial-gradient(circle at 60% 60%, #5a922c 10%, #5a922c00 20%), \n                radial-gradient(circle at 40% 60%, #4c7894 10%, #4c789400 20%),\n                repeating-conic-gradient(\n                  from 236.84deg at 50% 50%,\n                  #dd7bbb 0%,\n                  #d79f1e calc(25% / var(--repeating-conic-gradient-times)),\n                  #5a922c calc(50% / var(--repeating-conic-gradient-times)), \n                  #4c7894 calc(75% / var(--repeating-conic-gradient-times)),\n                  #dd7bbb calc(100% / var(--repeating-conic-gradient-times))\n                )";
  let t24;
  if ($[19] !== spread || $[20] !== t21 || $[21] !== t22 || $[22] !== t23) {
    t24 = {
      "--blur": t21,
      "--spread": spread,
      "--start": "0",
      "--active": "0",
      "--glowingeffect-border-width": t22,
      "--repeating-conic-gradient-times": "5",
      "--gradient": t23
    };
    $[19] = spread;
    $[20] = t21;
    $[21] = t22;
    $[22] = t23;
    $[23] = t24;
  } else {
    t24 = $[23];
  }
  const t25 = t24 as React.CSSProperties;
  const t26 = glow && "opacity-100";
  const t27 = blur > 0 && "blur-[var(--blur)] ";
  const t28 = disabled && "!hidden";
  let t29;
  if ($[24] !== className || $[25] !== t26 || $[26] !== t27 || $[27] !== t28) {
    t29 = cn("pointer-events-none absolute inset-0 rounded-[inherit] opacity-100 transition-opacity", t26, t27, className, t28);
    $[24] = className;
    $[25] = t26;
    $[26] = t27;
    $[27] = t28;
    $[28] = t29;
  } else {
    t29 = $[28];
  }
  let t30;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className={cn("glow", "rounded-[inherit]", "after:content-[\"\"] after:rounded-[inherit] after:absolute after:inset-[calc(-1*var(--glowingeffect-border-width))]", "after:[border:var(--glowingeffect-border-width)_solid_transparent]", "after:[background:var(--gradient)] after:[background-attachment:fixed]", "after:opacity-[var(--active)] after:transition-opacity after:duration-300", "after:[mask-clip:padding-box,border-box]", "after:[mask-composite:intersect]", "after:[mask-image:linear-gradient(#0000,#0000),conic-gradient(from_calc((var(--start)-var(--spread))*1deg),#00000000_0deg,#fff,#00000000_calc(var(--spread)*2deg))]")} />;
    $[29] = t30;
  } else {
    t30 = $[29];
  }
  let t31;
  if ($[30] !== t25 || $[31] !== t29) {
    t31 = <div ref={containerRef} style={t25} className={t29}>{t30}</div>;
    $[30] = t25;
    $[31] = t29;
    $[32] = t31;
  } else {
    t31 = $[32];
  }
  let t32;
  if ($[33] !== t20 || $[34] !== t31) {
    t32 = <>{t20}{t31}</>;
    $[33] = t20;
    $[34] = t31;
    $[35] = t32;
  } else {
    t32 = $[35];
  }
  return t32;
});
GlowingEffect.displayName = "GlowingEffect";
export { GlowingEffect };